import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
  TextField,
} from '@mui/material';
import { IconNotes, IconRefresh, IconPencil, IconX } from '@tabler/icons-react';
import { CollapsibleSection } from './CollapsibleSection';

interface CustomerNotesPanelProps {
  notes: string | null;
  isLoading: boolean;
  error: string | null;
  canEditNotes: boolean;
  refresh: () => void;
  update: (notes: string) => Promise<void>;
}

export const CustomerNotesPanel: React.FC<CustomerNotesPanelProps> = ({
  notes,
  isLoading,
  error,
  canEditNotes,
  refresh,
  update,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) {
      setEditValue(notes ?? '');
    }
  }, [editing, notes]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await update(editValue);
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setSaveError(null);
  };

  const hasNotes = Boolean(notes && notes.trim());

  return (
    <CollapsibleSection
      icon={IconNotes}
      title="Notes"
      expanded={expanded}
      onToggle={() => setExpanded(!expanded)}
    >
        {!editing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={refresh} disabled={isLoading}>
                <IconRefresh size={16} stroke={1.5} />
              </IconButton>
            </Tooltip>
            {canEditNotes && (
              <Tooltip title="Edit notes">
                <IconButton size="small" onClick={() => setEditing(true)}>
                  <IconPencil size={16} stroke={1.5} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={refresh}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {editing ? (
          <Box>
            <TextField
              multiline
              minRows={3}
              maxRows={10}
              fullWidth
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              disabled={saving}
              placeholder="Enter customer notes..."
              variant="outlined"
              size="small"
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={handleCancel}
                disabled={saving}
                startIcon={<IconX size={16} stroke={1.5} />}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={14} /> : undefined}
              >
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography
            variant="body2"
            color={hasNotes ? 'text.primary' : 'text.secondary'}
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {hasNotes ? notes : 'No notes'}
          </Typography>
        )}
    </CollapsibleSection>
  );
};
